import { DEFAULT_LOCALE, directionOf, getMessages, resolveLocale } from './index.js';

/**
 * Localization bootstrap (ENG-378): what has to happen before React renders.
 *
 * `bootstrapLocalization()` runs once from `src/index.jsx`, ahead of
 * `createRoot().render()`. It resolves the locale for this page load, puts
 * `<html lang dir>` in place so the very first paint is already in the right
 * language and direction — a right-to-left listener otherwise sees the
 * left-to-right shell from `index.html` for a frame and the layout jump when
 * `LocaleProvider` mounts — and builds the flat catalogs the translator
 * reads from so the first render does not pay for flattening (and, for a
 * pseudo-locale, deriving) them.
 *
 * `LocaleProvider` (`src/contexts/LocaleContext.jsx`) resolves from the same
 * sources in the same order, so it starts on the locale the bootstrap
 * applied without anything being threaded through props, and from then on
 * owns the locale: `setLocale` re-applies the document attributes through
 * `applyDocumentLocale` here.
 *
 * Resolution order (`localeCandidates`), first activatable candidate wins,
 * matched by exact tag and then by language (`en-GB` → `en-US`):
 *
 *   1. an explicit `preferred` tag — `LocaleProvider`'s `initialLocale` prop
 *      (tests, isolated mounts)
 *   2. the device copy in `localStorage` (`LOCALE_STORAGE_KEY`): the last
 *      choice made in this browser, or the mirror of the signed-in account's
 *      preference `LocalePreferenceSync` keeps (ENG-379)
 *   3. the browser's languages, most preferred first
 *   4. `DEFAULT_LOCALE`
 *
 * A signed-in listener's account preference is not a bootstrap source: it
 * needs a network round trip, so `LocalePreferenceSync` applies it after
 * mount and mirrors it to the device copy, which is why (2) usually already
 * says what the account will.
 */
export const LOCALE_STORAGE_KEY = 'locale';

/** The device copy of the locale choice, or null when none is stored or storage is unavailable. */
export const readStoredLocale = (storage = safeLocalStorage()) => {
  try {
    return storage?.getItem(LOCALE_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

/** Keep the device copy; a refusal (private mode, quota) is not an error — the choice still applies for this session. */
export const writeStoredLocale = (locale, storage = safeLocalStorage()) => {
  try {
    storage?.setItem(LOCALE_STORAGE_KEY, locale);
    return true;
  } catch {
    return false;
  }
};

/** The browser's languages, most preferred first (`navigator.language` last as a duplicate-safe fallback). */
export const browserLocales = (nav = typeof navigator === 'undefined' ? undefined : navigator) =>
  nav ? [...(nav.languages ?? []), nav.language].filter(Boolean) : [];

/** Every candidate tag for the initial locale, in resolution order, before validation. */
export const localeCandidates = (preferred, { storage, navigator: nav } = {}) => [
  preferred,
  readStoredLocale(storage),
  ...browserLocales(nav),
];

/** The locale this page load starts in — see the resolution order above. */
export const resolveInitialLocale = (preferred, sources) => resolveLocale(localeCandidates(preferred, sources));

/**
 * Mirror a locale onto the document so CSS (`[dir='rtl']`), assistive
 * technology and `Intl`-aware browser UI follow it. Returns the attributes
 * as they were, for a caller that wants to put them back.
 */
export const applyDocumentLocale = (locale, root = document.documentElement) => {
  const previous = { lang: root.getAttribute('lang'), dir: root.getAttribute('dir') };
  root.setAttribute('lang', locale);
  root.setAttribute('dir', directionOf(locale));
  return previous;
};

/** Undo `applyDocumentLocale`: restore what `index.html` (or the previous provider) had. */
export const restoreDocumentLocale = (previous, root = document.documentElement) => {
  for (const [name, value] of Object.entries(previous)) {
    if (value === null) root.removeAttribute(name);
    else root.setAttribute(name, value);
  }
};

/**
 * Resolve the locale for this page load, apply it to the document and warm
 * the catalogs it will read from. Returns `{ locale, dir }`.
 */
export const bootstrapLocalization = ({ preferred, root, ...sources } = {}) => {
  const locale = resolveInitialLocale(preferred, sources);
  applyDocumentLocale(locale, root);
  // The active catalog and the English fallback every miss is served from.
  getMessages(locale);
  getMessages(DEFAULT_LOCALE);
  return { locale, dir: directionOf(locale) };
};

function safeLocalStorage() {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    // Access itself throws when storage is disabled (some privacy modes).
    return undefined;
  }
}
